import {
  QBox,
  QButton,
  QCheckbox,
  QFormControl,
  QInput,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelect,
  QSelectPlaceholder,
  QStack,
  QText,
  useCurrentUser,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { documentApi } from '../../../../../api/document';
import {
  DocumentFormat,
  QualioDocument,
} from '../../../../../api/model/document';
import { useCCWorkflowData } from '../../../../../hooks/useCCWorkflowData';
import { useCompany } from '../../../../../hooks/useCompany';
import { useTrainingAssessment } from '../../../TrainingAssessment/hooks/useTrainingAssessment';
import { CCWorkflowLink } from '../../../../../types/DocumentCreate';
import { getApiErrorData } from '../../../../../util/ApiErrorUtils';
import {
  DOCUMENT_CODE_MAX_LENGTH,
  getDocumentCodeErrorMessageKey,
  getDocumentCodeErrorSlug,
} from '../../../../../util/DocumentCodeUtils';
import { DocumentOverviewContext } from '../../Context';
import {
  determineFileDownloadUsergroupPermissionsFromModel,
  determineFileDownloadUsergroupsPermissionsModelValue,
  getFileDownloadPermissionOptions,
} from '../utils';

type EditDocumentPropertiesModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type EditDocumentPropertiesForm = {
  title: QualioDocument['title'];
  code: QualioDocument['code'];
  ccWorkflow: string | null;
  reviewPeriod: string;
  effectiveOnApproval: QualioDocument['effective_on_approval'];
  retrainingRequired: QualioDocument['training_required'];
  fileDownloadUsergroups: 'owner' | 'quality' | 'normal' | 'read_only';
  trainingPassPercentage: string;
};

export const EditDocumentPropertiesModal = ({
  isOpen,
  onClose,
}: EditDocumentPropertiesModalProps) => {
  const { t } = useTranslation();
  const {
    qualioDocument,
    refetchDocumentOverviewData,
    changeControl,
    refetchChangeControl,
  } = useContext(DocumentOverviewContext);
  const { showToast } = useToastProvider();
  const [isSaving, setIsSaving] = useState(false);
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlag();
  const customDocumentCodes = Boolean(featureFlags['custom-document-codes']);
  const trainingPercentageComplete = Boolean(
    featureFlags['training-percentage-complete'],
  );

  const { allCCTemplates, linkedCCWorkflows, isLoadingLinkedWorkflows } =
    useCCWorkflowData(
      currentUser.companyId,
      qualioDocument.template_id,
      isOpen,
    );

  const ccWorkflowOptions = useMemo(() => {
    const linked = linkedCCWorkflows ?? [];

    if (linked.length === 0) {
      const systemTemplate = allCCTemplates.find(
        (t) => t.qualio_managed && t.status === 'effective',
      );
      return systemTemplate
        ? [
            {
              label: `${systemTemplate.name} (Default)`,
              value: String(systemTemplate.id),
            },
          ]
        : [];
    }

    return linked.map((wf: CCWorkflowLink) => ({
      label: wf.qualio_managed ? `${wf.name} (Default)` : wf.name,
      value: wf.id.toString(),
    }));
  }, [linkedCCWorkflows, allCCTemplates]);

  const commonToastProps = {
    id: 'edit-document-properties-toast',
    replace: true,
  };

  const hasMajorVersion = useMemo(
    () => qualioDocument.major_version > 0,
    [qualioDocument.major_version],
  );

  const canLoadTrainingPassPercentage =
    trainingPercentageComplete && isOpen && qualioDocument.training_available;

  const { data: trainingAssessment, isFetched: isTrainingAssessmentFetched } =
    useTrainingAssessment(qualioDocument.id, {
      enabled: canLoadTrainingPassPercentage,
      showErrorToast: false,
    });
  const assessmentPassPercentage =
    trainingAssessment?.training_assessment_completion_percent;

  const { data: company } = useCompany(
    canLoadTrainingPassPercentage &&
      isTrainingAssessmentFetched &&
      assessmentPassPercentage === undefined,
  );

  const resolvedPassPercentage =
    assessmentPassPercentage ?? company?.training_assessment_completion_percent;

  const defaultFormValues: EditDocumentPropertiesForm = useMemo(
    () => ({
      title: qualioDocument.title,
      code: qualioDocument.code,
      ccWorkflow: changeControl?.type?.toString() ?? null,
      reviewPeriod: (qualioDocument.review_period ?? 0).toString(),
      effectiveOnApproval: qualioDocument.effective_on_approval,
      retrainingRequired: qualioDocument.training_required,
      fileDownloadUsergroups:
        determineFileDownloadUsergroupPermissionsFromModel(qualioDocument),
      trainingPassPercentage:
        resolvedPassPercentage !== undefined
          ? String(resolvedPassPercentage)
          : '',
    }),
    [qualioDocument, changeControl, resolvedPassPercentage],
  );

  const {
    register,
    getValues,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<EditDocumentPropertiesForm>({
    defaultValues: defaultFormValues,
  });

  const retrainingRequired = useWatch({ control, name: 'retrainingRequired' });

  const showTrainingPassPercentage =
    trainingPercentageComplete &&
    qualioDocument.training_available &&
    Boolean(retrainingRequired) &&
    trainingAssessment !== undefined;

  const closeModal = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    reset(defaultFormValues, { keepDirtyValues: true });
  }, [defaultFormValues, reset]);

  const validateCode = (code: string) => {
    if (code === qualioDocument.code) return true;
    const errorSlug = getDocumentCodeErrorSlug(code);
    const messageKey = getDocumentCodeErrorMessageKey(errorSlug);
    return messageKey ? t(messageKey, { max: DOCUMENT_CODE_MAX_LENGTH }) : true;
  };

  const validatePassPercentage = (value: string) => {
    if (!showTrainingPassPercentage) return true;
    const trimmed = (value ?? '').trim();
    if (trimmed === '') {
      return resolvedPassPercentage === undefined
        ? true
        : t('forms.trainingPassPercentageInvalid');
    }
    if (!/^\d+$/.test(trimmed) || Number(trimmed) > 100) {
      return t('forms.trainingPassPercentageInvalid');
    }
    return true;
  };

  const onSave = async ({
    title,
    code,
    ccWorkflow,
    reviewPeriod,
    effectiveOnApproval,
    retrainingRequired: retrainingRequiredValue,
    fileDownloadUsergroups,
    trainingPassPercentage,
  }: EditDocumentPropertiesForm) => {
    setIsSaving(true);
    const updatedDocumentPayload = {
      title,
      review_period: reviewPeriod === '0' ? null : Number(reviewPeriod),
      effective_on_approval: effectiveOnApproval,
      training_required: retrainingRequiredValue,
      file_download_user_groups:
        determineFileDownloadUsergroupsPermissionsModelValue(
          fileDownloadUsergroups,
        ),
      ...(ccWorkflow && { change_control_template_id: Number(ccWorkflow) }),
      ...(customDocumentCodes ? { code } : {}),
      ...(showTrainingPassPercentage && trainingPassPercentage.trim() !== ''
        ? {
            training_assessment_completion_percent: Number(
              trainingPassPercentage,
            ),
          }
        : {}),
    };
    try {
      await documentApi
        .update(updatedDocumentPayload, qualioDocument.id, true)
        .then(() => {
          refetchDocumentOverviewData();
          refetchChangeControl?.();
          if (ccWorkflow) {
            void queryClient.invalidateQueries(['changeControlWithTemplate']);
          }
        });
      setIsSaving(false);
      showToast({
        'data-cy': 'edit-document-properties-modal-notify-success-toast',
        ...commonToastProps,
        status: 'success',
        title: t('notifications.propertiesUpdated'),
        description: t('notifications.successfullyUpdatedProperties'),
      });
      closeModal();
    } catch (e) {
      setIsSaving(false);
      const documentCodeErrorMessageKey = customDocumentCodes
        ? getDocumentCodeErrorMessageKey(getApiErrorData(e).slug)
        : undefined;
      showToast({
        'data-cy': 'edit-document-properties-modal-notify-error-toast',
        ...commonToastProps,
        status: 'error',
        title: t('notifications.error'),
        description: t(
          documentCodeErrorMessageKey ??
            'notifications.failedToUpdateProperties',
          { max: DOCUMENT_CODE_MAX_LENGTH },
        ),
      });
    }
  };

  const reviewPeriodOptions = useMemo(() => {
    const data = [];
    data.push({
      value: '0',
      label: t('labels.none'),
    });
    for (let i = 1; i <= 36; i++)
      data.push({
        value: i.toString(),
        label: i.toString(),
      });
    for (let j = 42; j <= 84; j += 6)
      data.push({
        value: j.toString(),
        label: j.toString(),
      });
    return data;
  }, [t]);

  const downloadPermissionsOptions = useMemo(() => {
    return Object.entries(getFileDownloadPermissionOptions(t)).map(
      ([value, label]) => ({
        value,
        label,
      }),
    );
  }, [t]);

  return (
    <QModal isOpen={isOpen} size="lg" onClose={closeModal}>
      <QModalHeader>
        <QText>{t('buttons.editProperties')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing="6">
          <QFormControl
            label={t('labels.title')}
            isInvalid={!!errors.title}
            error={errors.title?.message}
          >
            <QInput
              {...register('title', {
                required: t('forms.titleRequired'),
              })}
              placeholder={t('forms.documentTitle')}
              aria-label="Document title"
              data-cy="document-title-input"
            />
          </QFormControl>
          {customDocumentCodes && (
            <QFormControl
              label={t('labels.id')}
              helper={t('forms.documentCodeHelper', {
                max: DOCUMENT_CODE_MAX_LENGTH,
              })}
              isInvalid={!!errors.code}
              error={errors.code?.message}
            >
              <QInput
                {...register('code', { validate: validateCode })}
                placeholder={t('forms.documentIdentifier')}
                aria-label="Document ID"
                data-cy="document-code-input"
              />
            </QFormControl>
          )}
          <QFormControl label={t('forms.changeControlTemplate')}>
            {ccWorkflowOptions.length === 1 ? (
              <QText data-cy="cc-workflow-readonly">
                {ccWorkflowOptions[0].label}
              </QText>
            ) : (
              <Controller
                name="ccWorkflow"
                control={control}
                render={({ field: { onChange, ...fieldControl } }) => (
                  <QSelect
                    {...fieldControl}
                    isLoading={isLoadingLinkedWorkflows}
                    aria-label="Change control template"
                    data-cy="cc-workflow-selector"
                    size="sm"
                    options={ccWorkflowOptions}
                    onChange={(selected) => onChange(selected?.value ?? null)}
                  >
                    <QSelectPlaceholder>
                      <QText>{t('newDocument.ccWorkflowPlaceholder')}</QText>
                    </QSelectPlaceholder>
                  </QSelect>
                )}
              />
            )}
          </QFormControl>

          <QBox width="50%">
            <QFormControl
              id="document-review-period-select"
              label={t('forms.reviewPeriod')}
              helper={t('forms.monthsFromEffectiveDate')}
            >
              <Controller
                name="reviewPeriod"
                control={control}
                render={({ field: { ref, onChange, ...fieldControl } }) => {
                  return (
                    <QSelect
                      {...fieldControl}
                      options={reviewPeriodOptions}
                      size="sm"
                      aria-label="Review period"
                      data-cy="document-review-period-select"
                      onChange={(selectedReviewPeriod) => {
                        onChange(selectedReviewPeriod!.value);
                      }}
                    ></QSelect>
                  );
                }}
              />
            </QFormControl>
          </QBox>
          <QFormControl helper={t('forms.automaticallyMakesDocumentEffective')}>
            <Controller
              name="effectiveOnApproval"
              control={control}
              render={({ field: { ref, value, ...fieldControl } }) => {
                return (
                  <QCheckbox
                    {...fieldControl}
                    aria-label="Document effective on approval"
                    data-cy="document-effective-on-approval-checkbox"
                    isChecked={getValues().effectiveOnApproval}
                  >
                    {t('forms.effectiveOnApproval')}
                  </QCheckbox>
                );
              }}
            ></Controller>
          </QFormControl>
          {hasMajorVersion && qualioDocument.training_available && (
            <QFormControl>
              <Controller
                name="retrainingRequired"
                control={control}
                render={({ field: { ref, value, ...fieldControl } }) => {
                  return (
                    <QCheckbox
                      {...fieldControl}
                      aria-label="Training required on next major version"
                      data-cy="document-retraining-required-checkbox"
                      isChecked={getValues().retrainingRequired}
                    >
                      {t('forms.trainingRequiredOnNextMajorVersion')}
                    </QCheckbox>
                  );
                }}
              ></Controller>
            </QFormControl>
          )}
          {showTrainingPassPercentage && (
            <QBox width="100%">
              <QFormControl
                label={t('forms.trainingPassPercentage')}
                helper={t('forms.trainingPassPercentageHelper')}
                isInvalid={!!errors.trainingPassPercentage}
                error={errors.trainingPassPercentage?.message}
              >
                <QInput
                  {...register('trainingPassPercentage', {
                    validate: validatePassPercentage,
                  })}
                  type="text"
                  inputMode="numeric"
                  aria-label="Training assessment pass percentage"
                  data-cy="document-training-pass-percentage-input"
                />
              </QFormControl>
            </QBox>
          )}
          {qualioDocument.document_format === DocumentFormat.DocumentUpload && (
            <QFormControl
              id="document-download-permissions-select"
              label={t('forms.downloadPermissions')}
              helper={t('forms.permissionsToDownloadFile')}
            >
              <Controller
                name="fileDownloadUsergroups"
                control={control}
                render={({ field: { ref, onChange, ...fieldControl } }) => {
                  return (
                    <QSelect
                      {...fieldControl}
                      options={downloadPermissionsOptions}
                      size="sm"
                      aria-label="download permissions"
                      data-cy="document-download-permissions-select"
                      onChange={(selectedUsergroups) => {
                        onChange(selectedUsergroups!.value);
                      }}
                    />
                  );
                }}
              />
            </QFormControl>
          )}
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          data-cy="document-edit-properties-cancel-button"
          variant="outline"
          onClick={closeModal}
        >
          {t('buttons.cancel')}
        </QButton>
        <QButton
          data-cy="document-edit-properties-save-button"
          isLoading={isSaving}
          onClick={handleSubmit(onSave)}
        >
          {t('buttons.saveChanges')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
